/**
 * @module use-cases/checkout/actions
 * @desc Checkout Action Creators
 *
 */

import { createAction, createAsyncAction } from 'core/use-cases/utilities';

import type { Cart, Checkout, CreateOrder } from 'core/domains';
import type { ResponseBase, RewardProcessResponse, SelectedPromotion } from 'core/services/bff';

export const checkoutInit = createAction<'@@checkoutInit'>('@@checkoutInit');

export const checkoutCleanUp = createAction('checkoutCleanUp');

export const setPaymentMethodStatus = createAction('setPaymentMethodStatus');

export const createOrderFetch = createAsyncAction<
  'createOrderFetch',
  CreateOrder,
  Checkout['order']['code']
>('createOrderFetch');

export const createOrderFetchNewFlow = createAsyncAction<
  'createOrderFetchNewFlow',
  CreateOrder,
  Checkout['order']['code']
>('createOrderFetchNewFlow');

export const setCreditCardOption = createAction<'setCreditCardOption', Checkout['paymentOption']>(
  'setCreditCardOption',
);

export const setBankSlipOption = createAction<
  'setBankSlipOption',
  Checkout['paymentOption']['paymentCode']
>('setBankSlipOption');

export const setCentralPaymentOption = createAction<
  'setCentralPaymentOption',
  Checkout['paymentOption']['paymentCode']
>('setCentralPaymentOption');

export const setPayWithdrawalsOption = createAction<
  'setPayWithdrawalsOption',
  Checkout['paymentOption']['paymentCode']
>('setPayWithdrawalsOption');

export const setPaymentOption = createAction<
  'setPaymentOption',
  {
    code?: Checkout['paymentOption']['paymentCode'];
    mode?: Checkout['paymentOption']['paymentMode'];
    planType?: string;
  }
>('setPaymentOption');

export const setReady = createAction<'setReady', Checkout['isReady']>('setReady');

export const setStep = createAction<'setStep', Checkout['step']>('setStep');

export const setCreditCardCompleted = createAction<
  'setCreditCardCompleted',
  Checkout['paymentOption']
>('setCreditCardCompleted');

export const setView = createAction<'setView', string>('setView');

export const reloadPaymentMethods = createAction<'reloadPaymentMethods'>('reloadPaymentMethods');

export const paymentDetailFetch = createAsyncAction<
  'paymentDetailFetch',
  Cart['data']['paymentDetail'],
  Checkout['paymentDetail']['data']
>('paymentDetailFetch');

export const paymentDetailNewFlowFetch = createAsyncAction<
  'paymentDetailNewFlowFetch',
  Cart['data']['paymentDetail'],
  Cart['data']
>('paymentDetailNewFlowFetch');

export const paymentMethodsFetch = createAsyncAction<
  'paymentMethodsFetch',
  undefined,
  Checkout['paymentMethods']['data']
>('paymentMethodsFetch');

export const processFetch = createAsyncAction<'processFetch', undefined, RewardProcessResponse>(
  'processFetch',
);

export const promotionFetch = createAsyncAction<
  'promotionFetch',
  SelectedPromotion[],
  ResponseBase
>('promotionFetch');

export const toggleNotification = createAction('toggleNotification');

export const giftItemUpdate = createAction<
  'giftItemUpdate',
  SelectedPromotion & { method: string; index: number }
>('giftItemUpdate');

export const closeOrder = createAction<'closeOrder'>('closeOrder');

export const selectedGiftsCreate = createAction<'@@selectedGiftsCreate'>('@@selectedGiftsCreate');
